import { formatDateLabel, formatTimeLabel } from '../../../utils/date'
import { useReviewsAggregate } from '../../../hooks/useReviews'

export default function ExpertProfileSummaryCard({ astro, consultationType, selectedDate, selectedTime, birthDetailsValid = true, onProceed, isSubmitting = false }) {
  const canProceed = Boolean(selectedDate && selectedTime && birthDetailsValid) && !isSubmitting

  // The astrologer record's own rating/reviews_count aren't kept in sync
  // upstream (verified null even for astrologers with real reviews) — the
  // live GET /reviews aggregate is the source of truth.
  const { summary } = useReviewsAggregate('astrologer', astro.id)
  const ratingLabel = summary.totalReviews > 0 ? summary.averageRating.toFixed(1) : 'New'

  return (
    <div className="space-y-4">
      {/* <div className="flex items-center gap-3 h-8">
        <svg className="w-5 h-5 text-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <path strokeLinecap="round" strokeLinejoin="round" d="m9 14 2 2 4-4" />
        </svg>
        <span className="text-[10px] font-normal uppercase tracking-widest text-primary/40">Order Summary</span>
      </div> */}

      <div className="info-card overflow-hidden">
      <div className="h-0.75 w-full bg-linear-to-r from-[#C8922A] via-[#E5A820] to-[#C8922A]" />
      <div className="p-7 sm:p-8 space-y-6">
        <div className="flex items-center gap-4 pb-6 border-b border-[#D7C29A]/20">
          <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 border-2 border-secondary/30 shadow-[0_0_0_3px_rgba(242,160,7,.1)]">
            {astro.image ? (
              <img
                src={astro.image}
                alt={astro.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center font-cinzel text-xl font-bold"
                style={{ background: astro.fallbackGradient, color: 'rgba(120,50,0,.6)' }}
              >
                {astro.fallbackLetter}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-normal text-secondary uppercase tracking-widest mb-1">Expert Profile</p>
            <p className="text-lg font-cinzel text-[#1C140A] leading-tight truncate">{astro.name}</p>
            <p className="text-[10px] text-[#8B7355] mt-1 flex items-center gap-1.5">
              <span className="text-[#C8922A] font-bold">★ {ratingLabel}</span>
              <span className="opacity-40">·</span>
              <span className="truncate">{astro.role}</span>
            </p>
          </div>
          <div
            className="h-10 w-10 rounded-full flex items-center justify-center text-[#1C140A] shrink-0"
            style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F7D08F 100%)' }}
            title="Verified expert"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="m9 12 2 2 4-4" />
            </svg>
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-[#8B7355]">Consultation type</span>
            <span className="meta-pill">{consultationType}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[#8B7355]">Session fee</span>
            <span className="font-semibold text-[#1C140A]">{astro.priceLabel}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[#8B7355]">Platform fee</span>
            <span className="font-semibold text-[#1C140A]">₹0</span>
          </div>
        </div>

        <div className="astro-divider" />

        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold text-[#1C140A]">Total</span>
          <span className="text-3xl font-cinzel font-bold text-gradient-gold">{astro.priceLabel}</span>
        </div>

        <div className="space-y-2">
          <div
            className="flex items-center justify-between gap-2 p-3 rounded-lg border transition-colors"
            style={selectedDate ? { background: 'rgba(242,160,7,.06)', borderColor: 'rgba(242,160,7,.22)' } : { background: '#FAF6F0', borderColor: 'rgba(215,194,154,.35)' }}
          >
            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#8B7355]">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 2v4M16 2v4" />
                <rect width="18" height="18" x="3" y="4" rx="2" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18" />
              </svg>
              Date
            </span>
            <span className="text-xs font-semibold text-[#1C140A]">{selectedDate ? formatDateLabel(selectedDate) : 'Not selected'}</span>
          </div>
          <div
            className="flex items-center justify-between gap-2 p-3 rounded-lg border transition-colors"
            style={selectedTime ? { background: 'rgba(242,160,7,.06)', borderColor: 'rgba(242,160,7,.22)' } : { background: '#FAF6F0', borderColor: 'rgba(215,194,154,.35)' }}
          >
            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#8B7355]">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
              </svg>
              Time
            </span>
            <span className="text-xs font-semibold text-[#1C140A]">{selectedTime ? formatTimeLabel(selectedTime) : 'Not selected'}</span>
          </div>
        </div>

        <button
          type="button"
          disabled={!canProceed}
          onClick={onProceed}
          className="hero-btn-primary w-full inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.25em] disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Processing…' : 'Proceed to payment'}
        </button>

        <p className="text-center text-[10px] text-[#8B7355] flex items-center justify-center gap-1.5">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect width="18" height="11" x="3" y="11" rx="2" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Secure, encrypted checkout
        </p>

        {/* <div className="pt-2 flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-secondary animate-pulse" />
            <span className="text-[9px] font-normal uppercase tracking-widest text-primary/30">Secure, encrypted checkout</span>
          </div>
        </div> */}
      </div>
      </div>
    </div>
  )
}
